import { useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "../lib/api";
import SEO from "../components/SEO";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.name.length < 2 || !form.email.includes("@") || form.message.length < 10) {
            setError("Merci de remplir tous les champs (message ≥ 10 caractères).");
            return;
        }
        setStatus("loading");
        try {
            await api.post("/contact", { ...form, source: "page-contact" });
            setStatus("success");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            setStatus("error");
            setError(err?.response?.data?.detail || "Oups, réessayez dans un instant.");
        }
    };

    return (
        <div className="bg-cp-ivoire min-h-screen pt-28 pb-20" data-testid="contact-page">
            <SEO
                title="Nous écrire — Côte Perdue"
                description="Une question, une suggestion d'adresse, un partenariat ? Écrivez à la rédaction de Côte Perdue. Réponse sous 48 heures."
                path="/contact"
            />

            <section className="max-w-[900px] mx-auto px-6 md:px-12">
                <p className="cp-tiny text-cp-foret mb-8">Hors-série · Nous écrire</p>
                <h1 className="cp-serif text-5xl md:text-7xl lg:text-[88px] leading-[0.95] text-cp-encre tracking-[-0.025em]" data-testid="contact-title">
                    Vous avez <em className="cp-display-italic text-cp-foret">quelque chose</em><br />
                    à nous dire.
                </h1>
                <p className="cp-serif italic text-xl md:text-2xl font-light text-cp-gris leading-relaxed mt-12 max-w-2xl">
                    La rédaction lit chaque message. Une suggestion d'adresse oubliée, une correction sur une note, un projet de partenariat — écrivez-nous. Nous répondons généralement <em>sous 48 heures</em>.
                </p>

                <form onSubmit={submit} className="mt-16 space-y-10" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-8">
                        <label className="block">
                            <span className="cp-tiny text-cp-foret block mb-3">Votre nom</span>
                            <input
                                type="text"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Prénom Nom"
                                data-testid="contact-name-input"
                                className="w-full bg-transparent border-b border-cp-encre/30 py-3 cp-serif text-xl outline-none focus:border-cp-foret transition-colors"
                            />
                        </label>
                        <label className="block">
                            <span className="cp-tiny text-cp-foret block mb-3">Votre email</span>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="vous@adresse.fr"
                                data-testid="contact-email-input"
                                className="w-full bg-transparent border-b border-cp-encre/30 py-3 cp-serif text-xl outline-none focus:border-cp-foret transition-colors"
                            />
                        </label>
                    </div>
                    <label className="block">
                        <span className="cp-tiny text-cp-foret block mb-3">Sujet (optionnel)</span>
                        <input
                            type="text"
                            value={form.subject}
                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            placeholder="Suggestion · Partenariat · Correction..."
                            data-testid="contact-subject-input"
                            className="w-full bg-transparent border-b border-cp-encre/30 py-3 cp-serif text-xl outline-none focus:border-cp-foret transition-colors"
                        />
                    </label>
                    <label className="block">
                        <span className="cp-tiny text-cp-foret block mb-3">Votre message</span>
                        <textarea
                            required
                            rows={6}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Dites-nous tout."
                            data-testid="contact-message-input"
                            className="w-full bg-transparent border-b border-cp-encre/30 py-3 cp-serif text-xl outline-none focus:border-cp-foret transition-colors resize-none"
                        />
                    </label>

                    {error && <p data-testid="contact-error" className="cp-tiny text-red-700">{error}</p>}

                    {status === "success" ? (
                        <div className="bg-cp-craie p-8 border-l-4 border-cp-foret" data-testid="contact-success">
                            <p className="cp-script text-3xl text-cp-foret">Bien reçu, merci.</p>
                            <p className="cp-serif italic text-cp-gris mt-2">Vous allez recevoir un accusé de lecture par email. Nous vous répondons sous 48 heures.</p>
                        </div>
                    ) : (
                        <button
                            type="submit"
                            disabled={status === "loading"}
                            data-testid="contact-submit-button"
                            className="cp-tiny tracking-[0.3em] text-cp-encre border-b-2 border-cp-foret pb-1 hover:text-cp-foret transition-colors disabled:opacity-50"
                        >
                            {status === "loading" ? "ENVOI…" : "ENVOYER LE MESSAGE →"}
                        </button>
                    )}
                </form>

                <hr className="border-cp-border my-20" />

                <div className="space-y-4 cp-serif text-lg text-cp-gris">
                    <p><strong className="text-cp-encre">Rédaction</strong> · <a href="mailto:bonjour@cote-perdue.fr" className="underline hover:text-cp-foret">bonjour@cote-perdue.fr</a></p>
                    <p><strong className="text-cp-encre">Partenariats &amp; presse</strong> · <a href="mailto:partenaires@cote-perdue.fr" className="underline hover:text-cp-foret">partenaires@cote-perdue.fr</a></p>
                </div>
            </section>
        </div>
    );
}
